import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { STEP_VISUALS } from '../../how-it-works/stepVisuals';
import { useScrollStory } from '../../../hooks/useScrollStory';
import StepStage, { type StepAnnotation as Annotation } from '../../how-it-works/StepStage';

type Step = { title: string; body: string };

/** Scroll distance per step in the pinned story, in viewport heights. */
const VH_PER_STEP = 70;
function readArray<T>(raw: unknown, isItem: (o: Record<string, unknown>) => boolean): T[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((o): o is T => typeof o === 'object' && o !== null && isItem(o as Record<string, unknown>));
}

/**
 * The order lifecycle as a scroll story on desktop: the section pins and scrolling walks one order
 * from import to proof of delivery — layered visual, status annotations, stepper and copy all follow.
 * Phones and reduced-motion users get the static card grid. Step copy is shared with /how-it-works.
 */
export default function LifecycleStrip() {
  const { t, i18n } = useTranslation();
  const animate = useScrollStory();

  const steps = useMemo(
    () => readArray<Step>(t('howItWorksPage.steps', { returnObjects: true }), (o) => typeof o.title === 'string' && typeof o.body === 'string'),
    [t, i18n.language]
  );
  const annotations = useMemo(
    () =>
      readArray<Annotation>(t('home.lifecycle.annotations', { returnObjects: true }), (o) => typeof o.status === 'string' && typeof o.detail === 'string'),
    [t, i18n.language]
  );
  const shortLabels = useMemo(() => {
    const raw = t('home.lifecycle.stepShort', { returnObjects: true });
    return Array.isArray(raw) ? raw.filter((s): s is string => typeof s === 'string') : [];
  }, [t, i18n.language]);

  const header = (
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-blue-700">{t('home.lifecycle.eyebrow')}</p>
        <h2 id="lifecycle-title" className="mt-2 text-3xl font-bold tracking-tight text-gray-900 lg:text-4xl">
          {t('home.lifecycle.title')}
        </h2>
        {animate && <p className="mt-2 text-gray-500">{t('home.lifecycle.scrollHint')}</p>}
      </div>
      <Link to="/how-it-works" className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 hover:text-blue-600">
        {t('home.lifecycle.cta')}
        <i className="ri-arrow-right-line rtl:rotate-180" aria-hidden />
      </Link>
    </div>
  );

  if (!animate || steps.length === 0) {
    return <LifecycleGrid header={header} steps={steps} />;
  }
  return <LifecycleStory header={header} steps={steps} annotations={annotations} shortLabels={shortLabels} />;
}

function LifecycleStory({
  header,
  steps,
  annotations,
  shortLabels,
}: {
  header: ReactNode;
  steps: Step[];
  annotations: Annotation[];
  shortLabels: string[];
}) {
  const containerRef = useRef<HTMLElement>(null);
  const [progress, setProgress] = useState(0);
  const n = steps.length;
  const active = Math.min(n - 1, Math.floor(progress * n));

  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const scrollable = rect.height - window.innerHeight;
      setProgress(scrollable > 0 ? Math.min(1, Math.max(0, -rect.top / scrollable)) : 0);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  /** Jump to the middle of a step's scroll range. */
  const goTo = useCallback(
    (i: number) => {
      const el = containerRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY;
      const scrollable = el.offsetHeight - window.innerHeight;
      window.scrollTo({ top: top + ((i + 0.5) / n) * scrollable, behavior: 'smooth' });
    },
    [n]
  );

  const step = steps[active];
  const icon = STEP_VISUALS[active]?.icon ?? 'ri-checkbox-circle-line';

  return (
    <section
      ref={containerRef}
      className="relative bg-gradient-to-b from-white via-slate-50/70 to-white"
      style={{ height: `${n * VH_PER_STEP + 100}vh` }}
      aria-labelledby="lifecycle-title"
    >
      <div className="sticky top-16 flex h-[calc(100vh-4rem)] flex-col justify-center overflow-hidden">
        <div className="mx-auto w-full max-w-7xl px-6">
          <div className="mb-10">{header}</div>

          <div className="grid items-center gap-14 lg:grid-cols-12">
            {/* Stage: the previous step recedes behind the current one */}
            <div className="lg:col-span-7">
              <StepStage
                active={active}
                count={n}
                alts={steps.map((st) => st.title)}
                annotations={annotations}
                className="mx-auto max-h-[52vh] max-w-[calc(52vh*1.6)]"
              />
            </div>

            {/* Copy */}
            <div className="lg:col-span-5">
              <div key={active} className="opacity-0 animate-fade-in-up" aria-live="polite">
                <div className="flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-600 text-white">
                    <i className={`${icon} text-xl`} aria-hidden />
                  </span>
                  <span className="text-sm font-bold uppercase tracking-[0.18em] text-blue-700">
                    {String(active + 1).padStart(2, '0')} / {String(n).padStart(2, '0')}
                  </span>
                </div>
                <h3 className="mt-5 text-3xl font-bold tracking-tight text-gray-900">{step?.title}</h3>
                <p className="mt-4 text-lg leading-relaxed text-gray-600">{step?.body}</p>
              </div>
            </div>
          </div>

          {/* Stepper — fills continuously with scroll; dots jump to a step */}
          <div className="relative mt-12">
            <div className="absolute start-2.5 end-2.5 top-[9px] h-0.5 bg-gray-200" aria-hidden />
            <div
              className="absolute start-2.5 top-[9px] h-0.5 bg-gradient-to-r from-blue-600 to-emerald-500"
              style={{ width: `calc((100% - 1.25rem) * ${progress})` }}
              aria-hidden
            />
            <ol className="relative m-0 flex list-none justify-between p-0">
              {steps.map((s, i) => {
                const done = i < active;
                const current = i === active;
                return (
                  <li key={s.title}>
                    <button
                      type="button"
                      onClick={() => goTo(i)}
                      aria-current={current ? 'step' : undefined}
                      aria-label={s.title}
                      className="group flex flex-col items-center gap-2"
                    >
                      <span
                        className={`h-5 w-5 rounded-full border-2 transition-all duration-300 ${
                          current
                            ? 'scale-110 border-blue-600 bg-blue-600 ring-4 ring-blue-100'
                            : done
                              ? 'border-blue-600 bg-blue-600'
                              : 'border-gray-300 bg-white group-hover:border-blue-400'
                        }`}
                      />
                      <span className={`text-xs font-semibold ${current ? 'text-gray-900' : 'text-gray-400 group-hover:text-gray-600'}`}>
                        {shortLabels[i] ?? s.title}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
}

/** Static fallback: the six steps as linked cards. */
function LifecycleGrid({ header, steps }: { header: ReactNode; steps: Step[] }) {
  return (
    <section className="bg-white py-20 lg:py-24" aria-labelledby="lifecycle-title">
      <div className="mx-auto max-w-7xl px-6">
        <div className="mb-10">{header}</div>
        <ol className="m-0 grid list-none gap-5 p-0 sm:grid-cols-2 lg:grid-cols-3">
          {steps.map((step, i) => (
            <li key={step.title}>
              <Link
                to={`/how-it-works#step-${i + 1}`}
                className="group flex h-full flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white transition-all hover:-translate-y-0.5 hover:border-blue-300 hover:shadow-md"
              >
                {STEP_VISUALS[i] && (
                  <span className="flex aspect-[16/10] items-center justify-center overflow-hidden border-b border-gray-100 bg-slate-50 p-3">
                    <img src={STEP_VISUALS[i].img} alt="" loading="lazy" className="max-h-full max-w-full rounded-lg object-contain" />
                  </span>
                )}
                <span className="flex flex-1 items-center gap-3 p-4">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-600 text-white">
                    <i className={`${STEP_VISUALS[i]?.icon ?? 'ri-checkbox-circle-line'} text-base`} aria-hidden />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-xs font-bold uppercase tracking-wider text-gray-400">{String(i + 1).padStart(2, '0')}</span>
                    <span className="block font-semibold leading-snug text-gray-900 group-hover:text-blue-700">{step.title}</span>
                  </span>
                  <i className="ri-arrow-right-line text-gray-300 group-hover:text-blue-600 rtl:rotate-180" aria-hidden />
                </span>
              </Link>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}
